import { copyShareableUrl, formatCurrency, getQueryParams, updateQueryParams } from '../shared/calculator';
import type { CompoundResult } from '../shared/types';
import { calculateCompoundGrowth } from './compound';

interface ChartInstance {
  destroy(): void;
}

declare const Chart: new (
  ctx: HTMLCanvasElement,
  config: Record<string, unknown>,
) => ChartInstance;

const principalInput = document.getElementById('principal') as HTMLInputElement;
const monthlyInput = document.getElementById('monthly') as HTMLInputElement;
const rateInput = document.getElementById('rate') as HTMLInputElement;
const yearsInput = document.getElementById('years') as HTMLInputElement;
const frequencySelect = document.getElementById('frequency') as HTMLSelectElement;
const tbody = document.querySelector('#breakdown tbody') as HTMLTableSectionElement;

const params = getQueryParams({
  principal: '10000',
  monthly: '500',
  rate: '7',
  years: '25',
  frequency: 'monthly',
});
principalInput.value = params.principal;
monthlyInput.value = params.monthly;
rateInput.value = params.rate;
yearsInput.value = params.years;
frequencySelect.value = params.frequency;

let chart: ChartInstance | null = null;

function renderChart(result: CompoundResult): void {
  const labels = result.yearlyBreakdown.map((r) => `Year ${r.year}`);
  const balances = result.yearlyBreakdown.map((r) => r.endBalance);
  const contributions = result.yearlyBreakdown.map((r) => r.totalContributions);

  if (chart) chart.destroy();

  chart = new Chart(document.getElementById('growth-chart') as HTMLCanvasElement, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Total balance',
          data: balances,
          borderColor: '#2a9d8f',
          backgroundColor: 'rgba(42, 157, 143, 0.1)',
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Contributions only',
          data: contributions,
          borderColor: '#888',
          borderDash: [5, 5],
          fill: false,
          tension: 0.3,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: { dataset: { label: string }; parsed: { y: number } }) =>
              `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`,
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (v: number | string) => formatCurrency(Number(v)),
          },
        },
      },
    },
  });
}

function render(): void {
  const result = calculateCompoundGrowth({
    principal: Number(principalInput.value),
    monthlyContribution: Number(monthlyInput.value),
    annualRate: Number(rateInput.value),
    years: Number(yearsInput.value),
    compoundingFrequency: frequencySelect.value as CompoundResult['compoundingFrequency'],
  });

  updateQueryParams({
    principal: principalInput.value,
    monthly: monthlyInput.value,
    rate: rateInput.value,
    years: yearsInput.value,
    frequency: frequencySelect.value,
  });

  (document.getElementById('final-balance') as HTMLParagraphElement).textContent =
    formatCurrency(result.finalBalance);
  (document.getElementById('total-contributions') as HTMLParagraphElement).textContent =
    formatCurrency(result.totalContributions);
  (document.getElementById('total-interest') as HTMLParagraphElement).textContent =
    formatCurrency(result.totalInterest);

  tbody.innerHTML = result.yearlyBreakdown
    .map(
      (row) => `
        <tr>
          <td>${row.year}</td>
          <td>${formatCurrency(row.contributionsThisYear)}</td>
          <td>${formatCurrency(row.interestThisYear)}</td>
          <td>${formatCurrency(row.endBalance)}</td>
        </tr>
      `,
    )
    .join('');

  if (result.yearlyBreakdown.length) {
    renderChart(result);
  } else if (chart) {
    chart.destroy();
    chart = null;
  }
}

[principalInput, monthlyInput, rateInput, yearsInput, frequencySelect].forEach((el) => {
  el.addEventListener('input', render);
  el.addEventListener('change', render);
});

document.getElementById('share-btn')?.addEventListener('click', () => {
  copyShareableUrl({
    principal: principalInput.value,
    monthly: monthlyInput.value,
    rate: rateInput.value,
    years: yearsInput.value,
    frequency: frequencySelect.value,
  }).then(() => {
    const btn = document.getElementById('share-btn') as HTMLButtonElement;
    btn.textContent = '✅ Copied!';
    setTimeout(() => {
      btn.textContent = '📋 Copy shareable link';
    }, 2000);
  });
});

render();
